import React, { useRef, useState } from 'react';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CheckIcon from '@mui/icons-material/Check';
import { buildWhatsAppUrl, WHATSAPP_DISPLAY } from '../utils/whatsapp';
import { trackMetaEvent } from '../utils/metaPixel';
import type { LeadPayload, LeadTag } from '../types';
type FieldName = 'nombre' | 'whatsapp' | 'negocio' | 'situacion' | 'urgencia' | 'presupuesto';
type FormValues = Record<FieldName, string>;
const initialValues: FormValues = {
  nombre: '',
  whatsapp: '',
  negocio: '',
  situacion: '',
  urgencia: '',
  presupuesto: ''
};
const situacionOptions = [{
  value: 'sin-web',
  label: 'No tengo página web'
}, {
  value: 'web-vieja',
  label: 'Tengo una pero está vieja o no funciona'
}, {
  value: 'solo-redes',
  label: 'Tengo Facebook/Instagram pero no web'
}, {
  value: 'web-sin-clientes',
  label: 'Tengo web pero no me llegan clientes'
}];
const urgenciaOptions = [{
  value: 'esta-semana',
  label: 'Lo antes posible, esta semana 🔥'
}, {
  value: 'este-mes',
  label: 'Este mes'
}, {
  value: 'proximos-meses',
  label: 'En los próximos 2-3 meses'
}, {
  value: 'explorando',
  label: 'Solo estoy explorando por ahora'
}];
const presupuestoOptions = [{
  value: '600',
  label: 'Alrededor de $600 (Web + Google)'
}, {
  value: '1200',
  label: 'Alrededor de $1,200 (Web + CRM + Agenda)'
}, {
  value: '1800',
  label: 'Alrededor de $1,800 (Todo incluido)'
}, {
  value: 'no-seguro',
  label: 'Aún no estoy seguro, quiero orientación'
}];
function labelFor(options: {
  value: string;
  label: string;
}[], value: string): string {
  return options.find(option => option.value === value)?.label ?? value;
}
function buildLeadMessage(values: FormValues, tag: LeadTag): string {
  return [`¡Hola! Soy ${values.nombre} y quiero mi cotización de página web.`, '', `📌 Negocio: ${values.negocio}`, `📍 Situación hoy: ${labelFor(situacionOptions, values.situacion)}`, `⏱️ La necesito: ${labelFor(urgenciaOptions, values.urgencia)}`, `💰 Presupuesto: ${labelFor(presupuestoOptions, values.presupuesto)}`, `📱 Mi WhatsApp: ${values.whatsapp}`, '', `(Lead ${tag} · Ultimate Web Creator)`].join('\n');
}
function getLeadTag(urgencia: string, presupuesto: string): LeadTag {
  if ((urgencia === 'esta-semana' || urgencia === 'este-mes') && presupuesto !== 'no-seguro') {
    return 'HOT';
  }
  if (urgencia === 'proximos-meses') return 'WARM';
  return 'COLD';
}
const baseField = 'w-full rounded-[10px] border-2 px-4 py-3 text-sm text-ink outline-none transition focus:border-brand-blue focus:ring-[3px] focus:ring-brand-blue/10';
export function LeadForm() {
  const [values, setValues] = useState<FormValues>(initialValues);
  const [touched, setTouched] = useState<Partial<Record<FieldName, boolean>>>({});
  const [errors, setErrors] = useState<Partial<Record<FieldName, boolean>>>({});
  const [submitted, setSubmitted] = useState(false);
  const [whatsappUrl, setWhatsappUrl] = useState('');
  const leadRef = useRef<LeadPayload | null>(null);
  const leadTrackedRef = useRef(false);

  /** Se dispara únicamente cuando el usuario sale hacia WhatsApp. */
  const fireLeadEvent = () => {
    const lead = leadRef.current;
    if (leadTrackedRef.current || !lead) return;
    leadTrackedRef.current = true;
    const budget = Number(lead.presupuesto);
    trackMetaEvent('Lead', {
      content_name: `Cotización web · ${lead.negocio}`,
      content_category: lead.tag,
      value: Number.isFinite(budget) ? budget : 0,
      currency: 'USD'
    });
  };
  const setField = (name: FieldName, value: string) => {
    setValues(prev => ({
      ...prev,
      [name]: value
    }));
    if (value.trim() !== '') {
      setErrors(prev => ({
        ...prev,
        [name]: false
      }));
    }
  };
  const handleBlur = (name: FieldName) => {
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));
    setErrors(prev => ({
      ...prev,
      [name]: values[name].trim() === ''
    }));
  };
  const fieldState = (name: FieldName) => {
    if (errors[name]) return 'border-brand-red';
    if (touched[name] && values[name].trim() !== '') return 'border-brand-green';
    return 'border-line';
  };
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors: Partial<Record<FieldName, boolean>> = {};
    (Object.keys(values) as FieldName[]).forEach(key => {
      nextErrors[key] = values[key].trim() === '';
    });
    setErrors(nextErrors);
    setTouched({
      nombre: true,
      whatsapp: true,
      negocio: true,
      situacion: true,
      urgencia: true,
      presupuesto: true
    });
    if (Object.values(nextErrors).some(Boolean)) return;
    const tag = getLeadTag(values.urgencia, values.presupuesto);
    const payload: LeadPayload = {
      ...values,
      tag,
      timestamp: new Date().toISOString()
    };

    // Conecta aquí tu webhook de GoHighLevel:
    // fetch('TU_WEBHOOK_URL_GHL', { method: 'POST', body: JSON.stringify(payload) })
    // eslint-disable-next-line no-console
    console.log('Lead capturado:', payload);
    const url = buildWhatsAppUrl(buildLeadMessage(values, tag));
    leadRef.current = payload;
    setWhatsappUrl(url);
    setSubmitted(true);
    const whatsappWindow = window.open(url, '_blank');
    if (whatsappWindow) {
      whatsappWindow.opener = null;
      // La pestaña de WhatsApp sí se abrió: aquí sí es un Lead.
      fireLeadEvent();
    }
  };
  if (submitted) {
    return <div className="px-2 py-5 text-center" role="status" aria-live="polite" data-id="element-102" data-mp="2wldmarfvehz9">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-brand-green text-white" data-id="element-103" data-mp="2wngm236trph6">
          <CheckIcon sx={{
          fontSize: 32
        }} data-id="element-104" data-mp="uu0kib978q7f" />
        </div>
        <h3 className="mb-2 font-display text-[22px] font-extrabold text-ink" data-id="element-105" data-mp="uuijvgr5rkjk">
          ¡Listo! Te abrimos WhatsApp
        </h3>
        <p className="mb-5 text-sm leading-relaxed text-muted" data-id="element-106" data-mp="xe6twygl18vd">
          Tu mensaje ya viene escrito con los datos de tu negocio: solo tienes
          que presionar enviar y te respondemos con tu cotización exacta.
        </p>
        <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" onClick={fireLeadEvent} className="inline-flex w-full items-center justify-center gap-2.5 rounded-xl bg-[#25D366] px-4 py-4 font-display text-base font-extrabold text-white transition hover:-translate-y-0.5 hover:bg-[#1FB855] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2" data-id="element-107" data-mp="ocpap77vor2g">
          <WhatsAppIcon sx={{
          fontSize: 22
        }} data-id="element-108" data-mp="17a078k24l6e7" />
          Abrir WhatsApp ahora
        </a>
        <p className="mt-3 text-xs text-muted" data-id="element-109" data-mp="12b5mhckll7rg">
          ¿No se abrió? Escríbenos directo al{' '}
          <strong className="text-ink" data-id="element-110" data-mp="1zuqj4lq3mil2">{WHATSAPP_DISPLAY}</strong>
        </p>
      </div>;
  }
  return <div data-id="element-111" data-mp="1v04cevuype7b">
      <h2 className="mb-1.5 text-center font-display text-[22px] font-extrabold text-brand-deep" data-id="element-112" data-mp="22dbuvafvf26x">
        Cotiza Tu Web en 60 Seg
      </h2>
      <p className="mb-6 text-center text-[13px] text-muted" data-id="element-113" data-mp="27cy7bhnvkeeo">
        Responde 5 preguntas y te digo exactamente qué necesitas.{' '}
        <span className="font-semibold text-brand-blue" data-id="element-114" data-mp="2cdcj9aslk8hz">Sin rodeos.</span>
      </p>

      <form onSubmit={handleSubmit} noValidate data-id="element-115" data-mp="2hbnr5fzc08xk">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-id="element-116" data-mp="2lchf29crxnqg">
          <div className="mb-4" data-id="element-117" data-mp="2l9v5y5a8j064">
            <label className="mb-1.5 block text-[13px] font-semibold text-ink" htmlFor="nombre" data-id="element-118" data-mp="2qaiwyqygrh0c">
              Tu nombre
            </label>
            <input id="nombre" type="text" placeholder="Juan García" value={values.nombre} onChange={e => setField('nombre', e.target.value)} onBlur={() => handleBlur('nombre')} aria-invalid={Boolean(errors.nombre)} className={`${baseField} ${fieldState('nombre')}`} data-id="element-119" data-mp="300uc79nllcmj" />
          </div>
          <div className="mb-4" data-id="element-120" data-mp="3qb5h3od8363s">
            <label className="mb-1.5 block text-[13px] font-semibold text-ink" htmlFor="whatsapp" data-id="element-121" data-mp="3qa3uqnqrax6r">
              WhatsApp
            </label>
            <input id="whatsapp" type="tel" placeholder="(555) 000-0000" value={values.whatsapp} onChange={e => setField('whatsapp', e.target.value)} onBlur={() => handleBlur('whatsapp')} aria-invalid={Boolean(errors.whatsapp)} className={`${baseField} ${fieldState('whatsapp')}`} data-id="element-122" data-mp="35wfa924qhef" />
          </div>
        </div>

        <div className="mb-4" data-id="element-123" data-mp="3gjrev8tvzs6y">
          <label className="mb-1.5 block text-[13px] font-semibold text-ink" htmlFor="negocio" data-id="element-124" data-mp="2svhtu94hz8gv">
            ¿Qué tipo de negocio tienes?
          </label>
          <input id="negocio" type="text" placeholder="Ej: Techador, Dentista, Restaurante..." value={values.negocio} onChange={e => setField('negocio', e.target.value)} onBlur={() => handleBlur('negocio')} aria-invalid={Boolean(errors.negocio)} className={`${baseField} ${fieldState('negocio')}`} data-id="element-125" data-mp="2nukctzv84kgy" />
        </div>

        <SelectField id="situacion" label="¿Cuál es tu situación hoy?" value={values.situacion} options={situacionOptions} invalid={Boolean(errors.situacion)} stateClass={fieldState('situacion')} onChange={value => setField('situacion', value)} onBlur={() => handleBlur('situacion')} data-id="element-126" data-mp="1m4c779r2pula" />

        <SelectField id="urgencia" label="¿Para cuándo la necesitas?" value={values.urgencia} options={urgenciaOptions} invalid={Boolean(errors.urgencia)} stateClass={fieldState('urgencia')} onChange={value => setField('urgencia', value)} onBlur={() => handleBlur('urgencia')} data-id="element-127" data-mp="1p0g3otphbnq" />

        <SelectField id="presupuesto" label="¿Qué presupuesto tienes en mente?" value={values.presupuesto} options={presupuestoOptions} invalid={Boolean(errors.presupuesto)} stateClass={fieldState('presupuesto')} onChange={value => setField('presupuesto', value)} onBlur={() => handleBlur('presupuesto')} data-id="element-128" data-mp="1ovcg8b5z2t79" />

        <button type="submit" className="mt-5 flex w-full items-center justify-center gap-2.5 rounded-xl bg-brand-yellow px-4 py-4 font-display text-base font-extrabold text-brand-deep transition hover:-translate-y-0.5 hover:bg-brand-yellow-dark hover:shadow-[0_8px_30px_rgba(255,213,0,0.4)] active:translate-y-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2" data-id="element-129" data-mp="166xpzq3hbj7i">
          <RocketLaunchIcon sx={{
          fontSize: 20
        }} data-id="element-130" data-mp="31mnybiyhphqm" />
          Ver Mi Cotización por WhatsApp
        </button>

        <p className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-muted" data-id="element-131" data-mp="2wocov9c3x98x">
          <LockOutlinedIcon sx={{
          fontSize: 13
        }} data-id="element-132" data-mp="3afr9fklnxii3" />
          Tu información es privada. Nunca spam.
        </p>
      </form>
    </div>;
}
interface SelectFieldProps {
  id: string;
  label: string;
  value: string;
  options: {
    value: string;
    label: string;
  }[];
  invalid: boolean;
  stateClass: string;
  onChange: (value: string) => void;
  onBlur: () => void;
}
function SelectField({
  id,
  label,
  value,
  options,
  invalid,
  stateClass,
  onChange,
  onBlur
}: SelectFieldProps) {
  return <div className="mb-4" data-id="element-133" data-mp="3pbysghlc4dvs">
      <label className="mb-1.5 block text-[13px] font-semibold text-ink" htmlFor={id} data-id="element-134" data-mp="3pdjko0k6fkqh">
        {label}
      </label>
      <select id={id} value={value} onChange={e => onChange(e.target.value)} onBlur={onBlur} aria-invalid={invalid} className={`${baseField} ${stateClass} cursor-pointer appearance-none bg-[length:12px_8px] bg-[right_16px_center] bg-no-repeat pr-10 ${value === '' ? 'text-muted' : ''}`} style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%2364748B' stroke-width='2' fill='none'/%3E%3C/svg%3E\")"
    }} data-id="element-135" data-mp="1z1vuocyo7bre">
        <option value="" disabled data-id="element-136" data-mp="lva3ex4dz91s">
          Selecciona una opción
        </option>
        {options.map(option => <option key={option.value} value={option.value} data-id="element-137" data-mp="qnjvaip2aj6p">
            {option.label}
          </option>)}
      </select>
    </div>;
}